import React, { useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, Trash2, ArrowLeft, Pencil, Paperclip, FileText, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useSubscription } from '@/hooks/useSubscription';
import { uploadClinicImage } from '@/utils/cloudinary';

const StatusBadge = ({ status }) => {
  const statusColors = {
    'Scheduled': 'bg-blue-100 text-blue-800',
    'In Progress': 'bg-yellow-100 text-yellow-800',
    'Completed': 'bg-green-100 text-green-800',
    'Cancelled': 'bg-red-100 text-red-800'
  };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
};

export const VisitRecord = () => {
  const { visitId } = useParams();
  const [visit, setVisit] = useState(null);
  const [diagnoses, setDiagnoses] = useState([]);
  const [treatments, setTreatments] = useState([]);
  const [prescriptions, setPrescriptions] = useState([]);
  const [vaccinations, setVaccinations] = useState([]);
  const [visitServices, setVisitServices] = useState([]);
  const [owners, setOwners] = useState([]);
  const [pets, setPets] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [visitTypes, setVisitTypes] = useState([]);
  const [services, setServices] = useState([]);

  const [diagnosisDialog, setDiagnosisDialog] = useState(false);
  const [treatmentDialog, setTreatmentDialog] = useState(false);
  const [prescriptionDialog, setPrescriptionDialog] = useState(false);
  const [vaccinationDialog, setVaccinationDialog] = useState(false);
  const [serviceDialog, setServiceDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, title: '', onConfirm: null });

  const [diagnosisForm, setDiagnosisForm] = useState({ diagnosis_name: '', description: '', attachments: [] });
  const [treatmentForm, setTreatmentForm] = useState({ treatment_name: '', description: '', performed_by: '', attachments: [] });
  const [prescriptionForm, setPrescriptionForm] = useState({ medication_name: '', dosage: '', duration: '', instructions: '', attachments: [] });
  const [vaccinationForm, setVaccinationForm] = useState({ vaccine_name: '', manufacturer: '', batch_number: '', next_due_date: '', attachments: [] });
  const [serviceForm, setServiceForm] = useState({ service_id: '', price: '', quantity: '1', attachments: [] });

  const [editingDiagnosis, setEditingDiagnosis] = useState(null);
  const [editingTreatment, setEditingTreatment] = useState(null);
  const [editingPrescription, setEditingPrescription] = useState(null);
  const [editingVaccination, setEditingVaccination] = useState(null);
  const [fileUploading, setFileUploading] = useState(false);

  const { getAttachmentsLimit, loading: subscriptionLoading } = useSubscription();
  const attachmentsLimit = subscriptionLoading ? Infinity : getAttachmentsLimit();

  const fetchData = useCallback(async () => {
    try {
      const [visitResp, diagResp, treatResp, prescResp, vaccResp, serviceResp, ownersResp, petsResp, docsResp, typesResp, servicesResp] = await Promise.all([
        api.get(`/visits/${visitId}`),
        api.get(`/visits/${visitId}/diagnoses`),
        api.get(`/visits/${visitId}/treatments`),
        api.get(`/visits/${visitId}/prescriptions`),
        api.get(`/visits/${visitId}/vaccinations`),
        api.get(`/visits/${visitId}/services`),
        api.get('/owners'),
        api.get('/pets'),
        api.get('/doctors'),
        api.get('/visit-types'),
        api.get('/services')
      ]);

      setVisit(visitResp.data);
      setDiagnoses(diagResp.data);
      setTreatments(treatResp.data);
      setPrescriptions(prescResp.data);
      setVaccinations(vaccResp.data);
      setVisitServices(serviceResp.data);
      setOwners(ownersResp.data);
      setPets(petsResp.data);
      setDoctors(docsResp.data);
      setVisitTypes(typesResp.data);
      setServices(servicesResp.data);
    } catch (error) {
      toast.error('Failed to load visit details');
    }
  }, [visitId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddDiagnosis = async (e) => {
    e.preventDefault();
    try {
      if (editingDiagnosis) {
        await api.put(`/diagnoses/${editingDiagnosis.id}`, {
          ...diagnosisForm,
          visit_id: visitId,
          pet_id: visit.pet_id
        });
        toast.success('Diagnosis updated successfully');
      } else {
        await api.post('/visit-diagnoses', {
          ...diagnosisForm,
          visit_id: visitId,
          pet_id: visit.pet_id
        });
        toast.success('Diagnosis added successfully');
      }
      setDiagnosisForm({ diagnosis_name: '', description: '', attachments: [] });
      setEditingDiagnosis(null);
      setDiagnosisDialog(false);
      fetchData();
    } catch (error) {
      toast.error(editingDiagnosis ? 'Failed to update diagnosis' : 'Failed to add diagnosis');
    }
  };

  const handleAddTreatment = async (e) => {
    e.preventDefault();
    try {
      if (editingTreatment) {
        await api.put(`/treatments/${editingTreatment.id}`, {
          ...treatmentForm,
          visit_id: visitId,
          pet_id: visit.pet_id
        });
        toast.success('Treatment updated successfully');
      } else {
        await api.post('/treatments', {
          ...treatmentForm,
          visit_id: visitId,
          pet_id: visit.pet_id
        });
        toast.success('Treatment added successfully');
      }
      setTreatmentForm({ treatment_name: '', description: '', performed_by: '', attachments: [] });
      setEditingTreatment(null);
      setTreatmentDialog(false);
      fetchData();
    } catch (error) {
      toast.error(editingTreatment ? 'Failed to update treatment' : 'Failed to add treatment');
    }
  };

  const handleAddPrescription = async (e) => {
    e.preventDefault();
    try {
      if (editingPrescription) {
        await api.put(`/prescriptions/${editingPrescription.id}`, {
          ...prescriptionForm,
          visit_id: visitId,
          pet_id: visit.pet_id
        });
        toast.success('Prescription updated successfully');
      } else {
        await api.post('/prescriptions', {
          ...prescriptionForm,
          visit_id: visitId,
          pet_id: visit.pet_id
        });
        toast.success('Prescription added successfully');
      }
      setPrescriptionForm({ medication_name: '', dosage: '', duration: '', instructions: '', attachments: [] });
      setEditingPrescription(null);
      setPrescriptionDialog(false);
      fetchData();
    } catch (error) {
      toast.error(editingPrescription ? 'Failed to update prescription' : 'Failed to add prescription');
    }
  };

  const handleAddVaccination = async (e) => {
    e.preventDefault();
    try {
      if (editingVaccination) {
        await api.put(`/vaccinations/${editingVaccination.id}`, {
          ...vaccinationForm,
          visit_id: visitId,
          pet_id: visit.pet_id
        });
        toast.success('Vaccination updated successfully');
      } else {
        await api.post('/vaccinations', {
          ...vaccinationForm,
          visit_id: visitId,
          pet_id: visit.pet_id
        });
        toast.success('Vaccination added successfully');
      }
      setVaccinationForm({ vaccine_name: '', manufacturer: '', batch_number: '', next_due_date: '', attachments: [] });
      setEditingVaccination(null);
      setVaccinationDialog(false);
      fetchData();
    } catch (error) {
      toast.error(editingVaccination ? 'Failed to update vaccination' : 'Failed to add vaccination');
    }
  };

  const handleAddService = async (e) => {
    e.preventDefault();
    try {
      await api.post('/visit-services', {
        visit_id: visitId,
        pet_id: visit.pet_id,
        service_id: serviceForm.service_id,
        price: serviceForm.price ? parseFloat(serviceForm.price) : undefined,
        quantity: parseInt(serviceForm.quantity),
        attachments: serviceForm.attachments || [],
      });
      toast.success('Service added successfully');
      setServiceForm({ service_id: '', price: '', quantity: '1', attachments: [] });
      setServiceDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to add service');
    }
  };

  const handleEditDiagnosis = (diagnosis) => {
    setEditingDiagnosis(diagnosis);
    setDiagnosisForm({ diagnosis_name: diagnosis.diagnosis_name, description: diagnosis.description || '', attachments: diagnosis.attachments || [] });
    setDiagnosisDialog(true);
  };

  const handleEditTreatment = (treatment) => {
    setEditingTreatment(treatment);
    setTreatmentForm({ treatment_name: treatment.treatment_name, description: treatment.description || '', performed_by: treatment.performed_by || '', attachments: treatment.attachments || [] });
    setTreatmentDialog(true);
  };

  const handleEditPrescription = (prescription) => {
    setEditingPrescription(prescription);
    setPrescriptionForm({ medication_name: prescription.medication_name, dosage: prescription.dosage, duration: prescription.duration, instructions: prescription.instructions || '', attachments: prescription.attachments || [] });
    setPrescriptionDialog(true);
  };

  const handleEditVaccination = (vaccination) => {
    setEditingVaccination(vaccination);
    setVaccinationForm({ vaccine_name: vaccination.vaccine_name, manufacturer: vaccination.manufacturer, batch_number: vaccination.batch_number, next_due_date: vaccination.next_due_date, attachments: vaccination.attachments || [] });
    setVaccinationDialog(true);
  };

  const handleDeleteDiagnosis = (diagnosisId) => {
    setDeleteDialog({
      open: true,
      title: 'Delete this diagnosis?',
      onConfirm: async () => {
        try {
          await api.delete(`/diagnoses/${diagnosisId}`);
          toast.success('Diagnosis deleted');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete diagnosis');
        }
      },
    });
  };

  const handleDeleteTreatment = (treatmentId) => {
    setDeleteDialog({
      open: true,
      title: 'Delete this treatment?',
      onConfirm: async () => {
        try {
          await api.delete(`/treatments/${treatmentId}`);
          toast.success('Treatment deleted');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete treatment');
        }
      },
    });
  };

  const handleDeletePrescription = (prescriptionId) => {
    setDeleteDialog({
      open: true,
      title: 'Delete this prescription?',
      onConfirm: async () => {
        try {
          await api.delete(`/prescriptions/${prescriptionId}`);
          toast.success('Prescription deleted');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete prescription');
        }
      },
    });
  };

  const handleDeleteVaccination = (vaccinationId) => {
    setDeleteDialog({
      open: true,
      title: 'Delete this vaccination record?',
      onConfirm: async () => {
        try {
          await api.delete(`/vaccinations/${vaccinationId}`);
          toast.success('Vaccination deleted');
          fetchData();
        } catch (error) {
          toast.error('Failed to delete vaccination');
        }
      },
    });
  };

  const handleDeleteService = (serviceId) => {
    setDeleteDialog({
      open: true,
      title: 'Remove this service?',
      onConfirm: async () => {
        try {
          await api.delete(`/visit-services/${serviceId}`);
          toast.success('Service removed');
          fetchData();
        } catch (error) {
          toast.error('Failed to remove service');
        }
      },
    });
  };

  const handleFileSelect = async (e, currentAttachments, setForm, formState) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (currentAttachments.length >= attachmentsLimit) {
      toast.error(`Maximum ${attachmentsLimit} file${attachmentsLimit === 1 ? '' : 's'} allowed on your plan`);
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be under 10 MB');
      return;
    }
    setFileUploading(true);
    try {
      const url = await uploadClinicImage(file, 'records');
      setForm(prevForm => ({
        ...prevForm,
        attachments: [ ...(prevForm.attachments || []), url ],
      }));
      toast.success('File uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setFileUploading(false);
    }
  };

  const isImageUrl = (url) => /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url);
  const getUrlFilename = (url) => {
    try { return decodeURIComponent(url.split('/').pop().split('?')[0]); } catch { return url.split('/').pop(); }
  };

  const FileAttachmentsList = ({ attachments, onRemove, onAdd, formState }) => (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Attachments</Label>
        <span className="text-xs text-gray-500">{attachments.length}/{attachmentsLimit} files</span>
      </div>
      {attachments.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {attachments.map((url, i) => (
            <div key={i} className="flex items-center gap-1 bg-gray-100 rounded-lg px-2 py-1 text-sm max-w-full">
              {isImageUrl(url)
                ? <img src={url} alt="" className="w-6 h-6 object-cover rounded flex-shrink-0" />
                : <FileText className="w-4 h-4 text-blue-600 flex-shrink-0" />}
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline truncate max-w-[140px]">
                {getUrlFilename(url)}
              </a>
              <button type="button" onClick={() => onRemove(i)} className="text-red-400 hover:text-red-600 ml-1 flex-shrink-0">
                <XIcon className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      {attachments.length < attachmentsLimit ? (
        <label className={`flex items-center gap-2 px-3 py-2 border-2 border-dashed border-gray-300 rounded-xl cursor-pointer hover:border-sage-400 transition-colors${fileUploading ? ' opacity-50 pointer-events-none' : ''}`}>
          <Paperclip className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600">{fileUploading ? 'Uploading…' : 'Add image or document'}</span>
          <input
            type="file"
            className="hidden"
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
            onChange={(e) => onAdd(e, attachments, formState)}
            disabled={fileUploading}
          />
        </label>
      ) : (
        <p className="text-xs text-amber-600">
          {attachmentsLimit === 1
            ? 'Upgrade to Basic to upload up to 5 files per record.'
            : `Maximum ${attachmentsLimit} files reached.`}
        </p>
      )}
    </div>
  );

  if (!visit) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg text-gray-600">Loading...</div>
      </div>
    );
  }

  const ownerName = owners.find((o) => o.id === visit.owner_id)?.full_name || 'Unknown';
  const petName = pets.find((p) => p.id === visit.pet_id)?.name || 'Unknown';
  const doctorName = doctors.find((d) => d.id === visit.veterinarian_id)?.full_name || 'Not assigned';
  const visitTypeName = visitTypes.find((t) => t.id === visit.visit_type_id)?.name || 'Unknown';

  return (
    <div className="p-6 md:p-12 min-h-screen bg-gray-50">
      <div className="mb-6 flex items-center gap-4">
        <Button variant="ghost" className="rounded-full" onClick={() => window.history.back()}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-4xl font-heading font-bold text-sage-800">{visitTypeName}</h1>
          <p className="text-gray-600">{petName} - {ownerName}</p>
        </div>
      </div>

      {/* Visit Overview */}
      <div className="bg-white rounded-3xl shadow-lg p-8 mb-6">
        <div className="flex flex-wrap gap-3 justify-between items-start mb-6">
          <div>
            <h2 className="text-2xl font-heading font-bold text-sage-800 mb-4">Visit Summary</h2>
          </div>
          <StatusBadge status={visit.status} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
          <div>
            <p className="text-sm text-gray-500 mb-1">Pet</p>
            <p className="font-semibold text-gray-800">{petName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Owner</p>
            <p className="font-semibold text-gray-800">{ownerName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Veterinarian</p>
            <p className="font-semibold text-gray-800">Dr. {doctorName}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500 mb-1">Visit Date</p>
            <p className="font-semibold text-gray-800">{new Date(visit.visit_date).toLocaleString()}</p>
          </div>
        </div>

        {visit.notes && (
          <div className="mt-6 pt-6 border-t">
            <p className="text-sm text-gray-500 mb-2">Notes</p>
            <p className="text-gray-800">{visit.notes}</p>
          </div>
        )}
      </div>

      {/* Medical Records */}
      <Tabs defaultValue="diagnoses" className="w-full">
        <TabsList className="flex flex-wrap w-full max-w-3xl gap-1 mb-6 h-auto">
          <TabsTrigger value="diagnoses" className="rounded-full">Diagnoses</TabsTrigger>
          <TabsTrigger value="treatments" className="rounded-full">Treatments</TabsTrigger>
          <TabsTrigger value="prescriptions" className="rounded-full">Prescriptions</TabsTrigger>
          <TabsTrigger value="vaccinations" className="rounded-full">Vaccinations</TabsTrigger>
          <TabsTrigger value="services" className="rounded-full">Services</TabsTrigger>
        </TabsList>

        {/* Diagnoses Tab */}
        <TabsContent value="diagnoses">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-heading font-bold text-sage-800">Diagnoses</h3>
              <Dialog open={diagnosisDialog} onOpenChange={(open) => { setDiagnosisDialog(open); if (!open) { setEditingDiagnosis(null); setDiagnosisForm({ diagnosis_name: '', description: '', attachments: [] }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-sage-500 hover:bg-sage-600 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Diagnosis
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader>
                    <DialogTitle>{editingDiagnosis ? 'Edit Diagnosis' : 'Add Diagnosis'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddDiagnosis} className="space-y-4">
                    <div>
                      <Label>Diagnosis Name</Label>
                      <Input
                        value={diagnosisForm.diagnosis_name}
                        onChange={(e) => setDiagnosisForm({ ...diagnosisForm, diagnosis_name: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="e.g., Parvovirus"
                        required
                      />
                    </div>
                    <div>
                      <Label>Description (Optional)</Label>
                      <Textarea
                        value={diagnosisForm.description}
                        onChange={(e) => setDiagnosisForm({ ...diagnosisForm, description: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="Additional details..."
                      />
                    </div>
                    <FileAttachmentsList
                      attachments={diagnosisForm.attachments || []}
                      onRemove={(i) => setDiagnosisForm({ ...diagnosisForm, attachments: (diagnosisForm.attachments || []).filter((_, idx) => idx !== i) })}
                      onAdd={(e, atts, fs) => handleFileSelect(e, atts, setDiagnosisForm, fs)}
                      formState={diagnosisForm}
                    />
                    <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white">
                      {editingDiagnosis ? 'Update Diagnosis' : 'Save Diagnosis'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {diagnoses.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No diagnoses recorded</p>
              ) : (
                diagnoses.map((diagnosis) => (
                  <div key={diagnosis.id} className="border rounded-2xl p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800 mb-1">{diagnosis.diagnosis_name}</h4>
                        {diagnosis.description && <p className="text-gray-600 text-sm">{diagnosis.description}</p>}
                        {diagnosis.attachments && diagnosis.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {diagnosis.attachments.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 rounded-lg px-2 py-1 hover:bg-blue-100 transition-colors">
                                {isImageUrl(url) ? <img src={url} alt="" className="w-4 h-4 object-cover rounded" /> : <FileText className="w-3 h-3" />}
                                <span className="truncate max-w-[100px]">{getUrlFilename(url)}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditDiagnosis(diagnosis)}
                          className="text-sage-600 hover:text-sage-700"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteDiagnosis(diagnosis.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Treatments Tab */}
        <TabsContent value="treatments">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-heading font-bold text-sage-800">Treatments</h3>
              <Dialog open={treatmentDialog} onOpenChange={(open) => { setTreatmentDialog(open); if (!open) { setEditingTreatment(null); setTreatmentForm({ treatment_name: '', description: '', performed_by: '', attachments: [] }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-sage-500 hover:bg-sage-600 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Treatment
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader>
                    <DialogTitle>{editingTreatment ? 'Edit Treatment' : 'Add Treatment'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddTreatment} className="space-y-4">
                    <div>
                      <Label>Treatment Name</Label>
                      <Input
                        value={treatmentForm.treatment_name}
                        onChange={(e) => setTreatmentForm({ ...treatmentForm, treatment_name: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="e.g., Antibiotics"
                        required
                      />
                    </div>
                    <div>
                      <Label>Description (Optional)</Label>
                      <Textarea
                        value={treatmentForm.description}
                        onChange={(e) => setTreatmentForm({ ...treatmentForm, description: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="Treatment details..."
                      />
                    </div>
                    <div>
                      <Label>Performed By (Optional)</Label>
                      <Input
                        value={treatmentForm.performed_by}
                        onChange={(e) => setTreatmentForm({ ...treatmentForm, performed_by: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="Staff member name"
                      />
                    </div>
                    <FileAttachmentsList
                      attachments={treatmentForm.attachments || []}
                      onRemove={(i) => setTreatmentForm({ ...treatmentForm, attachments: (treatmentForm.attachments || []).filter((_, idx) => idx !== i) })}
                      onAdd={(e, atts, fs) => handleFileSelect(e, atts, setTreatmentForm, fs)}
                      formState={treatmentForm}
                    />
                    <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white">
                      {editingTreatment ? 'Update Treatment' : 'Save Treatment'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {treatments.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No treatments recorded</p>
              ) : (
                treatments.map((treatment) => (
                  <div key={treatment.id} className="border rounded-2xl p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800 mb-1">{treatment.treatment_name}</h4>
                        {treatment.description && <p className="text-gray-600 text-sm mb-1">{treatment.description}</p>}
                        {treatment.performed_by && <p className="text-gray-500 text-sm">By: {treatment.performed_by}</p>}
                        {treatment.attachments && treatment.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {treatment.attachments.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 rounded-lg px-2 py-1 hover:bg-blue-100 transition-colors">
                                {isImageUrl(url) ? <img src={url} alt="" className="w-4 h-4 object-cover rounded" /> : <FileText className="w-3 h-3" />}
                                <span className="truncate max-w-[100px]">{getUrlFilename(url)}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditTreatment(treatment)}
                          className="text-sage-600 hover:text-sage-700"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteTreatment(treatment.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Prescriptions Tab */}
        <TabsContent value="prescriptions">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-heading font-bold text-sage-800">Prescriptions</h3>
              <Dialog open={prescriptionDialog} onOpenChange={(open) => { setPrescriptionDialog(open); if (!open) { setEditingPrescription(null); setPrescriptionForm({ medication_name: '', dosage: '', duration: '', instructions: '', attachments: [] }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-sage-500 hover:bg-sage-600 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Prescription
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader>
                    <DialogTitle>{editingPrescription ? 'Edit Prescription' : 'Add Prescription'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddPrescription} className="space-y-4">
                    <div>
                      <Label>Medication Name</Label>
                      <Input
                        value={prescriptionForm.medication_name}
                        onChange={(e) => setPrescriptionForm({ ...prescriptionForm, medication_name: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="Medication name"
                        required
                      />
                    </div>
                    <div>
                      <Label>Dosage</Label>
                      <Input
                        value={prescriptionForm.dosage}
                        onChange={(e) => setPrescriptionForm({ ...prescriptionForm, dosage: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="e.g., 500mg"
                        required
                      />
                    </div>
                    <div>
                      <Label>Duration</Label>
                      <Input
                        value={prescriptionForm.duration}
                        onChange={(e) => setPrescriptionForm({ ...prescriptionForm, duration: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="e.g., 7 days"
                        required
                      />
                    </div>
                    <div>
                      <Label>Instructions (Optional)</Label>
                      <Textarea
                        value={prescriptionForm.instructions}
                        onChange={(e) => setPrescriptionForm({ ...prescriptionForm, instructions: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="e.g., Take with food"
                      />
                    </div>
                    <FileAttachmentsList
                      attachments={prescriptionForm.attachments || []}
                      onRemove={(i) => setPrescriptionForm({ ...prescriptionForm, attachments: (prescriptionForm.attachments || []).filter((_, idx) => idx !== i) })}
                      onAdd={(e, atts, fs) => handleFileSelect(e, atts, setPrescriptionForm, fs)}
                      formState={prescriptionForm}
                    />
                    <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white">
                      {editingPrescription ? 'Update Prescription' : 'Save Prescription'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {prescriptions.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No prescriptions recorded</p>
              ) : (
                prescriptions.map((prescription) => (
                  <div key={prescription.id} className="border rounded-2xl p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800 mb-2">{prescription.medication_name}</h4>
                        <div className="grid grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Dosage</p>
                            <p className="font-semibold">{prescription.dosage}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Duration</p>
                            <p className="font-semibold">{prescription.duration}</p>
                          </div>
                        </div>
                        {prescription.instructions && <p className="text-gray-600 text-sm mt-2 italic">{prescription.instructions}</p>}
                        {prescription.attachments && prescription.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {prescription.attachments.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 rounded-lg px-2 py-1 hover:bg-blue-100 transition-colors">
                                {isImageUrl(url) ? <img src={url} alt="" className="w-4 h-4 object-cover rounded" /> : <FileText className="w-3 h-3" />}
                                <span className="truncate max-w-[100px]">{getUrlFilename(url)}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditPrescription(prescription)}
                          className="text-sage-600 hover:text-sage-700"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeletePrescription(prescription.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Vaccinations Tab */}
        <TabsContent value="vaccinations">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-heading font-bold text-sage-800">Vaccinations</h3>
              <Dialog open={vaccinationDialog} onOpenChange={(open) => { setVaccinationDialog(open); if (!open) { setEditingVaccination(null); setVaccinationForm({ vaccine_name: '', manufacturer: '', batch_number: '', next_due_date: '', attachments: [] }); } }}>
                <DialogTrigger asChild>
                  <Button className="bg-sage-500 hover:bg-sage-600 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vaccination
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader>
                    <DialogTitle>{editingVaccination ? 'Edit Vaccination' : 'Add Vaccination'}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddVaccination} className="space-y-4">
                    <div>
                      <Label>Vaccine Name</Label>
                      <Input
                        value={vaccinationForm.vaccine_name}
                        onChange={(e) => setVaccinationForm({ ...vaccinationForm, vaccine_name: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="e.g., Rabies"
                        required
                      />
                    </div>
                    <div>
                      <Label>Manufacturer</Label>
                      <Input
                        value={vaccinationForm.manufacturer}
                        onChange={(e) => setVaccinationForm({ ...vaccinationForm, manufacturer: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="Manufacturer name"
                        required
                      />
                    </div>
                    <div>
                      <Label>Batch Number</Label>
                      <Input
                        value={vaccinationForm.batch_number}
                        onChange={(e) => setVaccinationForm({ ...vaccinationForm, batch_number: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="Batch/Lot number"
                        required
                      />
                    </div>
                    <div>
                      <Label>Next Due Date</Label>
                      <Input
                        type="date"
                        value={vaccinationForm.next_due_date}
                        onChange={(e) => setVaccinationForm({ ...vaccinationForm, next_due_date: e.target.value })}
                        className="rounded-xl mt-2"
                        required
                      />
                    </div>
                    <FileAttachmentsList
                      attachments={vaccinationForm.attachments || []}
                      onRemove={(i) => setVaccinationForm({ ...vaccinationForm, attachments: (vaccinationForm.attachments || []).filter((_, idx) => idx !== i) })}
                      onAdd={(e, atts, fs) => handleFileSelect(e, atts, setVaccinationForm, fs)}
                      formState={vaccinationForm}
                    />
                    <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white">
                      {editingVaccination ? 'Update Vaccination' : 'Save Vaccination'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {vaccinations.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No vaccinations recorded</p>
              ) : (
                vaccinations.map((vaccination) => (
                  <div key={vaccination.id} className="border rounded-2xl p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-800 mb-2">{vaccination.vaccine_name}</h4>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="text-gray-500">Manufacturer</p>
                            <p className="font-semibold">{vaccination.manufacturer}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Batch Number</p>
                            <p className="font-semibold">{vaccination.batch_number}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Next Due Date</p>
                            <p className="font-semibold">{vaccination.next_due_date}</p>
                          </div>
                        </div>
                        {vaccination.attachments && vaccination.attachments.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-2">
                            {vaccination.attachments.map((url, i) => (
                              <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 rounded-lg px-2 py-1 hover:bg-blue-100 transition-colors">
                                {isImageUrl(url) ? <img src={url} alt="" className="w-4 h-4 object-cover rounded" /> : <FileText className="w-3 h-3" />}
                                <span className="truncate max-w-[100px]">{getUrlFilename(url)}</span>
                              </a>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditVaccination(vaccination)}
                          className="text-sage-600 hover:text-sage-700"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteVaccination(vaccination.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services">
          <div className="bg-white rounded-3xl shadow-lg p-8">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-2xl font-heading font-bold text-sage-800">Services</h3>
              <Dialog open={serviceDialog} onOpenChange={setServiceDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-sage-500 hover:bg-sage-600 text-white rounded-full">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Service
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader>
                    <DialogTitle>Add Service to Visit</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddService} className="space-y-4">
                    <div>
                      <Label>Service</Label>
                      <select
                        value={serviceForm.service_id}
                        onChange={(e) => setServiceForm({ ...serviceForm, service_id: e.target.value })}
                        className="w-full rounded-xl mt-2 px-3 py-2 border"
                        required
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service.id} value={service.id}>
                            {service.name} - ${service.price.toFixed(2)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        value={serviceForm.quantity}
                        onChange={(e) => setServiceForm({ ...serviceForm, quantity: e.target.value })}
                        className="rounded-xl mt-2"
                        min="1"
                        required
                      />
                    </div>
                    <div>
                      <Label>Custom Price (Optional)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={serviceForm.price}
                        onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value })}
                        className="rounded-xl mt-2"
                        placeholder="Leave blank to use service price"
                      />
                    </div>
                    <FileAttachmentsList
                      attachments={serviceForm.attachments || []}
                      onRemove={(i) => setServiceForm({ ...serviceForm, attachments: (serviceForm.attachments || []).filter((_, idx) => idx !== i) })}
                      onAdd={(e, atts, fs) => handleFileSelect(e, atts, setServiceForm, fs)}
                      formState={serviceForm}
                    />
                    <Button type="submit" className="w-full rounded-full bg-sage-500 hover:bg-sage-600 text-white">
                      Add Service
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <div className="space-y-4">
              {visitServices.length === 0 ? (
                <p className="text-gray-600 text-center py-8">No services added</p>
              ) : (
                visitServices.map((vs) => {
                  const service = services.find((s) => s.id === vs.service_id);
                  return (
                    <div key={vs.id} className="border rounded-2xl p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h4 className="font-bold text-gray-800 mb-1">{service?.name || 'Unknown Service'}</h4>
                          <p className="text-gray-600 text-sm">Quantity: {vs.quantity}</p>
                          <p className="text-gray-700 font-semibold mt-1">Price: ${(vs.price || service?.price || 0).toFixed(2)}</p>
                          {vs.attachments && vs.attachments.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-2">
                              {vs.attachments.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs bg-blue-50 text-blue-700 rounded-lg px-2 py-1 hover:bg-blue-100 transition-colors">
                                  {isImageUrl(url) ? <img src={url} alt="" className="w-4 h-4 object-cover rounded" /> : <FileText className="w-3 h-3" />}
                                  <span className="truncate max-w-[100px]">{getUrlFilename(url)}</span>
                                </a>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteService(vs.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog((d) => ({ ...d, open }))}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog.title} This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteDialog.onConfirm) await deleteDialog.onConfirm();
                setDeleteDialog({ open: false, title: '', onConfirm: null });
              }}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
